import { Link } from "react-router-dom";
import { ArrowRight, Phone, Check } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { LeadForm } from "../components/LeadForm";
import { SITE, img, openEnquiry } from "../config";
import { trackEvent } from "../analytics";
import { CONFIGURATIONS, HIGHLIGHTS, AMENITIES, AMENITY_LIST, CONNECTIVITY, FAQS } from "../data/site";

const TITLE = "Atlanta Enclave Shilphata | 1, 2 & 3 BHK Flats";
const DESC =
    "Atlanta Enclave Shilphata – premium 1, 2 & 3 BHK flats in Shilphata, Thane by Atlanta Limited. RERA P51700077788. Check price, floor plans, amenities & location. Book a site visit.";

const JSON_LD = [
    {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: TITLE,
        url: "https://atlantaenclave.com/",
        description: DESC,
        isPartOf: { "@type": "WebSite", name: "Atlanta Enclave", url: "https://atlantaenclave.com/" },
    },
    {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    },
];

const MARQUEE = [
    "RERA Registered — P51700077788",
    "8 Acre Gated Community",
    "Five Elegant Towers",
    "60% Open Spaces",
    "MIVAN Construction",
    "By Atlanta Limited — Since 1977",
];

export default function Home() {
    return (
        <div data-testid="home-page">
            <Seo title={TITLE} description={DESC} path="/" jsonLd={JSON_LD} />

            {/* Hero */}
            <section className="container-x pt-10 sm:pt-16" data-testid="hero-section">
                <Reveal>
                    <p className="eyebrow">Shilphata · Thane · RERA {SITE.rera}</p>
                    <h1 className="font-display mt-4 max-w-3xl text-4xl font-medium leading-[1.08] text-[#23231f] sm:text-5xl lg:text-6xl">
                        Atlanta Enclave Shilphata — 1, 2 &amp; 3 BHK Flats in Shilphata
                    </h1>
                    <p className="mt-5 max-w-xl text-base text-[#55534a] sm:text-lg">
                        An 8-acre gated community of five towers where luxury meets nature&apos;s serenity — sunlit homes
                        with private sun decks, world-class amenities and seamless connectivity to Mumbai, Navi Mumbai and Thane.
                    </p>
                    <div className="mt-7 flex flex-wrap gap-3">
                        <button onClick={openEnquiry} className="btn btn-gold" data-testid="hero-get-price-button">
                            Get Price &amp; Floor Plan
                        </button>
                        <Link to="/contact/" className="btn btn-line" data-testid="hero-site-visit-button">
                            Book Site Visit
                        </Link>
                    </div>
                </Reveal>
                <Reveal className="mt-10" delay={120}>
                    <img
                        src={img("hero")}
                        alt="Atlanta Enclave Shilphata – spacious 1, 2 and 3 BHK bed sundeck residences from ₹49.99 Lakhs"
                        className="frame-img w-full"
                        width="1536"
                        height="675"
                        fetchpriority="high"
                        data-testid="hero-image"
                    />
                </Reveal>
            </section>

            {/* Marquee strip */}
            <div className="mt-12 overflow-hidden border-y border-[#ded8c6] bg-[#efeadb] py-3" aria-hidden="true">
                <div className="marquee-track">
                    {[...MARQUEE, ...MARQUEE].map((m, i) => (
                        <span key={i} className="flex items-center gap-8 pr-8 text-xs font-bold uppercase tracking-[0.22em] text-[#6e6234]">
                            {m} <span className="text-[#b8a45e]">✦</span>
                        </span>
                    ))}
                </div>
            </div>

            {/* Highlights */}
            <section className="container-x mt-20" data-testid="highlights-section">
                <Reveal>
                    <p className="eyebrow">01 — Project Highlights</p>
                    <h2 className="sec-title mt-3">Why Atlanta Enclave Shilphata</h2>
                </Reveal>
                <div className="mt-8 grid gap-px border border-[#ded8c6] bg-[#ded8c6] sm:grid-cols-2 lg:grid-cols-4">
                    {HIGHLIGHTS.map((h, i) => (
                        <Reveal key={h.value} delay={i * 80} className="bg-[#f7f4ec]">
                            <div className="p-6">
                                <p className="font-display text-3xl font-semibold text-[#6e6234]">{h.value}</p>
                                <p className="mt-2 text-sm text-[#55534a]">{h.label}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* About */}
            <section className="container-x mt-20 grid items-center gap-10 lg:grid-cols-2" data-testid="about-section">
                <Reveal>
                    <p className="eyebrow">02 — About</p>
                    <h2 className="sec-title mt-3">Where Luxury Meets Nature&apos;s Serenity</h2>
                    <p className="mt-5 text-sm leading-relaxed text-[#55534a] sm:text-base">
                        Welcome to Atlanta Enclave Shilphata, where luxury meets serenity in the heart of Thane. Nestled in the
                        thriving locale of Shilphata, this premium residential project offers spacious 1, 2 and 3 BHK apartments
                        designed for modern living — surrounded by lush greenery and equipped with world-class amenities.
                    </p>
                    <ul className="mt-6 grid gap-3">
                        {[
                            "Prime location with seamless connectivity to Mumbai, Pune and Thane",
                            "Spacious, sunlit residences with private sun decks",
                            "Sustainable, eco-friendly design amid natural surroundings",
                            "Built by Atlanta Limited — delivering quality since 1977",
                        ].map((li) => (
                            <li key={li} className="flex items-start gap-3 text-sm text-[#23231f] sm:text-base">
                                <Check size={17} className="mt-0.5 shrink-0 text-[#8f7f45]" /> {li}
                            </li>
                        ))}
                    </ul>
                </Reveal>
                <Reveal delay={120}>
                    <img
                        src={img("elevation")}
                        alt="Atlanta Enclave Shilphata elevation – premium residential towers in Thane"
                        className="frame-img w-full"
                        loading="lazy"
                        width="1139"
                        height="688"
                        data-testid="about-elevation-image"
                    />
                </Reveal>
            </section>

            {/* Configurations */}
            <section id="configurations" className="container-x mt-20 scroll-mt-24" data-testid="configurations-section">
                <Reveal>
                    <p className="eyebrow">03 — Residences &amp; Price</p>
                    <h2 className="sec-title mt-3">Atlanta Enclave Price &amp; Configurations</h2>
                    <p className="mt-3 max-w-xl text-sm text-[#55534a] sm:text-base">
                        Transparent pricing for every family size. Prices are subject to change; flexible payment plans available.
                    </p>
                </Reveal>
                <div className="mt-8 grid gap-6 md:grid-cols-3">
                    {CONFIGURATIONS.map((c, i) => (
                        <Reveal key={c.key} delay={i * 90}>
                            <article className="card-line flex h-full flex-col p-6" data-testid={`config-card-${c.key}`}>
                                <p className="eyebrow">{c.area}</p>
                                <h3 className="font-display mt-2 text-2xl font-semibold text-[#23231f]">Atlanta Enclave {c.name}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-[#55534a]">{c.blurb}</p>
                                <p className="font-display mt-5 text-2xl font-semibold text-[#6e6234]">
                                    {c.price} <span className="text-sm font-normal text-[#8a877a]">{c.priceNote}</span>
                                </p>
                                <Link
                                    to={c.path}
                                    className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#23231f] underline decoration-[#8f7f45] underline-offset-4 hover:text-[#6e6234]"
                                    data-testid={`config-link-${c.key}`}
                                >
                                    Explore Atlanta Enclave {c.name} <ArrowRight size={15} />
                                </Link>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* Amenities */}
            <section className="mt-20 bg-[#2e2a1c] py-16 text-[#e8e3d3]" data-testid="amenities-section">
                <div className="container-x">
                    <Reveal>
                        <p className="eyebrow !text-[#b8a45e]">04 — Amenities</p>
                        <h2 className="sec-title mt-3 !text-white">A Recreational Paradise</h2>
                        <p className="mt-3 max-w-xl text-sm text-[#b8b29b] sm:text-base">
                            From a dip in the swimming pool to a workout in the state-of-the-art fitness center — every day at
                            Atlanta Enclave rejuvenates body and mind.
                        </p>
                    </Reveal>
                    <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                        {AMENITIES.slice(0, 4).map((a, i) => (
                            <Reveal key={a.name} delay={i * 80}>
                                <figure className="border border-[#4a4430] bg-[#38331f] p-3">
                                    <img src={img(a.image)} alt={a.alt} className="aspect-square w-full object-cover" loading="lazy" width="236" height="236" />
                                    <figcaption className="mt-3 text-sm font-semibold text-white">{a.name}</figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-6">
                        <ul className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#b8b29b] sm:grid-cols-3">
                            {AMENITY_LIST.map((a) => (
                                <li key={a} className="flex items-center gap-2">
                                    <span className="h-1 w-1 rounded-full bg-[#b8a45e]" /> {a}
                                </li>
                            ))}
                        </ul>
                        <Link to="/amenities/" className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#b8a45e] hover:text-white" data-testid="home-amenities-link">
                            View all Atlanta Enclave amenities <ArrowRight size={15} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* Master Plan */}
            <section id="master-plan" className="container-x mt-20 grid scroll-mt-24 items-center gap-10 lg:grid-cols-2" data-testid="master-plan-section">
                <Reveal>
                    <img src={img("master-plan")} alt="Atlanta Enclave Shilphata master plan – 8 acre gated community with five towers" className="frame-img w-full" loading="lazy" width="1156" height="524" data-testid="master-plan-image" />
                </Reveal>
                <Reveal delay={120}>
                    <p className="eyebrow">05 — Master Plan</p>
                    <h2 className="sec-title mt-3">A Blueprint for Elevated Living</h2>
                    <p className="mt-5 text-sm leading-relaxed text-[#55534a] sm:text-base">
                        The Atlanta Enclave Shilphata master plan spans over 8 acres of prime land with five elegant towers that
                        harmonize modern architecture with natural beauty — a layout optimized for privacy, ventilation and
                        sunlight, with over 60% of the area dedicated to landscaped gardens and open spaces.
                    </p>
                    <Link to="/floor-plans/" className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#23231f] underline decoration-[#8f7f45] underline-offset-4 hover:text-[#6e6234]" data-testid="master-plan-floorplans-link">
                        See Atlanta Enclave floor plans <ArrowRight size={15} />
                    </Link>
                </Reveal>
            </section>

            {/* Location */}
            <section className="container-x mt-20 grid items-center gap-10 lg:grid-cols-2" data-testid="location-section">
                <Reveal>
                    <p className="eyebrow">06 — Location &amp; Connectivity</p>
                    <h2 className="sec-title mt-3">Shilphata — Perfectly Positioned</h2>
                    <p className="mt-5 text-sm leading-relaxed text-[#55534a] sm:text-base">
                        Atlanta Enclave Shilphata is strategically located at the foothills of Thane, offering unmatched
                        connectivity — surrounded by nature yet close to top schools, hospitals, malls and entertainment hubs.
                    </p>
                    <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
                        {CONNECTIVITY.slice(0, 6).map(([place, time]) => (
                            <li key={place} className="flex items-baseline justify-between gap-3 border-b border-[#ded8c6] pb-2 text-sm">
                                <span className="text-[#23231f]">{place}</span>
                                <span className="font-bold text-[#6e6234]">{time}</span>
                            </li>
                        ))}
                    </ul>
                    <Link to="/location/" className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#23231f] underline decoration-[#8f7f45] underline-offset-4 hover:text-[#6e6234]" data-testid="home-location-link">
                        Explore Atlanta Enclave location <ArrowRight size={15} />
                    </Link>
                </Reveal>
                <Reveal delay={120}>
                    <img src={img("location-map")} alt="Atlanta Enclave Shilphata location map – connectivity to Thane, Mumbai and Navi Mumbai" className="frame-img w-full" loading="lazy" width="601" height="579" data-testid="location-map-image" />
                </Reveal>
            </section>

            {/* RERA */}
            <section className="container-x mt-20" data-testid="rera-section">
                <Reveal>
                    <div className="card-line flex flex-col items-start gap-6 p-6 sm:flex-row sm:items-center sm:p-8">
                        <img src={img("rera-qr")} alt="Atlanta Enclave RERA QR code – P51700077788" className="h-28 w-28 border border-[#ded8c6]" loading="lazy" width="350" height="350" data-testid="rera-qr-image" />
                        <div>
                            <p className="eyebrow">07 — RERA</p>
                            <h2 className="sec-title mt-2 !text-2xl">RERA Registered — {SITE.rera}</h2>
                            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#55534a]">
                                Atlanta Enclave Shilphata is registered with the Maharashtra Real Estate Regulatory Authority
                                (MahaRERA). Verify the registration at{" "}
                                <a href={SITE.reraPortal} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#6e6234] underline underline-offset-2" data-testid="rera-portal-link">
                                    maharera.mahaonline.gov.in
                                </a>
                                .
                            </p>
                        </div>
                    </div>
                </Reveal>
            </section>

            {/* FAQs */}
            <section className="container-x mt-20" data-testid="faq-section">
                <Reveal>
                    <p className="eyebrow">08 — FAQs</p>
                    <h2 className="sec-title mt-3">Frequently Asked Questions</h2>
                </Reveal>
                <div className="mt-8 grid gap-3">
                    {FAQS.map((f, i) => (
                        <Reveal key={f.q} delay={i * 40}>
                            <details className="faq-item card-line p-5" data-testid={`faq-item-${i}`}>
                                <summary className="text-sm font-bold text-[#23231f] sm:text-base">{f.q}</summary>
                                <p className="mt-3 text-sm leading-relaxed text-[#55534a]">{f.a}</p>
                            </details>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* Enquiry */}
            <section id="enquire" className="container-x mt-20 grid scroll-mt-24 items-start gap-10 lg:grid-cols-2" data-testid="enquiry-section">
                <Reveal>
                    <p className="eyebrow">09 — Enquire</p>
                    <h2 className="sec-title mt-3">Get Price, Floor Plans &amp; Brochure</h2>
                    <p className="mt-4 max-w-md text-sm leading-relaxed text-[#55534a] sm:text-base">
                        Share your details and our team will call you back with the latest Atlanta Enclave price, floor plans,
                        brochure and availability — or schedule a site visit today.
                    </p>
                    <div className="mt-6 grid gap-3">
                        <a href={SITE.phoneTel} onClick={() => trackEvent("call_click", { placement: "home_enquiry" })} className="btn btn-dark w-fit" data-testid="enquiry-call-button">
                            <Phone size={16} /> Call {SITE.phoneDisplay}
                        </a>
                    </div>
                </Reveal>
                <Reveal delay={120}>
                    <LeadForm source="home_page" idPrefix="home" heading="Enquire About Atlanta Enclave" />
                </Reveal>
            </section>
        </div>
    );
}
